import { useState } from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, Mail, MapPin, User, Package, CheckCircle, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const products = [
  { id: 'cocoalife-30', name: 'CocoaLife™ 30-Day Supply', price: 65.00 },
  { id: 'cocoalife-60', name: 'CocoaLife™ 60-Day Supply', price: 80.00 },
];

export default function CocoaLifeOrderPage() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    product: 'cocoalife-30',
    quantity: '1',
    notes: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);

  const selectedProduct = products.find(p => p.id === form.product);
  const quantity = parseInt(form.quantity) || 1;
  const subtotal = selectedProduct ? selectedProduct.price * quantity : 0;
  const shipping = quantity >= 2 ? 0 : 6.00;
  const total = subtotal + shipping;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.email || !form.address || !form.city || !form.state || !form.zip) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/cocoalife-order`, {
        ...form,
        quantity: parseInt(form.quantity),
        product_name: selectedProduct?.name,
        subtotal,
        shipping,
        total
      });
      setOrderComplete(true);
      toast.success('Order submitted successfully!');
    } catch (error) {
      console.error('Order error:', error);
      toast.error('Failed to submit order. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (orderComplete) {
    return (
      <div className="min-h-screen py-24 md:py-32" data-testid="cocoalife-order-page">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-lg mx-auto text-center"
          >
            <div className="w-24 h-24 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Order Received!
            </h1>
            <Card className="bg-card border-accent/30 mb-8">
              <CardContent className="p-6 text-left">
                <h3 className="font-bold text-lg mb-4 text-accent">What's Next?</h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-accent font-bold">•</span>
                    <span>Your order will be prepared within <strong className="text-foreground">3 business days</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent font-bold">•</span>
                    <span>Shipping takes approximately <strong className="text-foreground">7 business days</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent font-bold">•</span>
                    <span>A confirmation email has been sent to <strong className="text-foreground">{form.email}</strong></span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            <p className="text-muted-foreground mb-8">
              Thank you for your order! We'll be in touch soon.
            </p>
            <Link to="/">
              <Button className="btn-primary">Back to Home</Button>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 md:py-32" data-testid="cocoalife-order-page">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-4">
            <ShoppingBag className="w-8 h-8 text-accent" />
          </div>
          <h1 className="section-header mb-4">CocoaLife™ Order Form</h1>
          <p className="section-subheader max-w-xl mx-auto">
            Order your CocoaLife™ chocolate supplement directly. Fast processing, reliable shipping.
          </p>
        </motion.div>

        <div className="max-w-2xl mx-auto">
          <form onSubmit={handleSubmit}>
            <Card className="bg-card border-border/50 mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <User className="w-5 h-5 text-accent" />
                  Contact Information
                </h2>
                <div className="grid gap-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border/50 mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-accent" />
                  Shipping Address
                </h2>
                <div className="grid gap-4">
                  <div>
                    <Label htmlFor="address">Street Address *</Label>
                    <Input
                      id="address"
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      placeholder="123 Main St"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="col-span-2 md:col-span-1">
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        value={form.city}
                        onChange={(e) => setForm({ ...form, city: e.target.value })}
                        placeholder="City"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State *</Label>
                      <Input
                        id="state"
                        value={form.state}
                        onChange={(e) => setForm({ ...form, state: e.target.value })}
                        placeholder="OR"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="zip">ZIP Code *</Label>
                      <Input
                        id="zip"
                        value={form.zip}
                        onChange={(e) => setForm({ ...form, zip: e.target.value })}
                        placeholder="97701"
                        required
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border/50 mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <Package className="w-5 h-5 text-accent" />
                  Order Details
                </h2>
                <div className="grid gap-4">
                  <div>
                    <Label htmlFor="product">Product *</Label>
                    <Select value={form.product} onValueChange={(v) => setForm({ ...form, product: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name} - ${p.price.toFixed(2)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="quantity">Quantity *</Label>
                    <Select value={form.quantity} onValueChange={(v) => setForm({ ...form, quantity: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6].map((q) => (
                          <SelectItem key={q} value={q.toString()}>
                            {q} {q >= 2 && '(Free Shipping!)'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="notes">Special Instructions (Optional)</Label>
                    <Textarea
                      id="notes"
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Any special requests or notes..."
                      rows={3}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Order Summary */}
            <Card className="bg-card border-accent/30 mb-6" style={{ boxShadow: '0 0 30px rgba(244, 196, 48, 0.1)' }}>
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-4">Order Summary</h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{selectedProduct?.name} x {quantity}</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Shipping</span>
                    <span className={shipping === 0 ? 'text-green-500' : ''}>
                      {shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}
                    </span>
                  </div>
                  {quantity < 2 && (
                    <p className="text-xs text-accent">Order 2+ for FREE shipping!</p>
                  )}
                  <hr className="border-border my-2" />
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="text-accent">${total.toFixed(2)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-muted/30 border-border/50 mb-6">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground text-center">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Orders are processed within <strong>3 business days</strong>. 
                  Shipping takes approximately <strong>7 business days</strong>.
                </p>
              </CardContent>
            </Card>

            <Button
              type="submit"
              className="w-full h-14 text-lg btn-primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <ShoppingBag className="w-5 h-5 mr-2" />
                  Place Order - ${total.toFixed(2)}
                </>
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground mt-4">
              Payment instructions will be sent via email after order confirmation.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
